import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, KeyRound, Power, X, ScrollText, Loader2 } from "lucide-react";

const empty = { name: "", email: "", password: "", trainer_name: "", can_see_all: true, active: true };

const TrainerManager = () => {
  const [list, setList] = useState(null);
  const [form, setForm] = useState(empty);
  const [creating, setCreating] = useState(false);
  const [pwFor, setPwFor] = useState(null);
  const [pw, setPw] = useState("");
  const [logFor, setLogFor] = useState(null);
  const [logs, setLogs] = useState([]);

  const load = () => api.get("/admin/trainers").then((r) => setList(r.data)).catch(() => setList([]));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post("/admin/trainers", form);
      toast.success("Trainerkonto erstellt");
      setForm(empty); load();
    } catch (err) { toast.error(apiError(err)); } finally { setCreating(false); }
  };

  const update = async (id, patch) => {
    try { await api.put(`/admin/trainers/${id}`, patch); load(); toast.success("Gespeichert"); }
    catch (err) { toast.error(apiError(err)); }
  };

  const resetPw = async () => {
    if (pw.length < 6) { toast.error("Mindestens 6 Zeichen"); return; }
    await update(pwFor.id, { password: pw });
    setPwFor(null); setPw("");
  };

  const del = async (t) => {
    if (!window.confirm(`Trainerkonto „${t.name}" wirklich löschen?`)) return;
    try { await api.delete(`/admin/trainers/${t.id}`); load(); toast.success("Gelöscht"); }
    catch (err) { toast.error(apiError(err)); }
  };

  const openLog = async (t) => {
    setLogFor(t);
    const { data } = await api.get(`/admin/trainers/${t.id}/access-log`);
    setLogs(data);
  };

  if (!list) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#ff0033]" /></div>;

  return (
    <div data-testid="trainer-manager">
      <h2 className="font-heading text-2xl uppercase tracking-tight text-white">Trainerzugänge</h2>
      <p className="text-neutral-500 text-sm mt-1 mb-6">Zugänge für das Pro Portal (read-only) verwalten. Login unter <code className="text-[#ff0033]">/pro-login</code>.</p>

      {/* Neues Konto */}
      <form onSubmit={create} className="bg-[#0F0F0F] border border-white/10 p-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6" data-testid="trainer-create-form">
        <input required placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#1A1A1A] border border-white/10 text-white p-2.5 outline-none focus:border-[#ff0033]" data-testid="trainer-name" />
        <input required type="email" placeholder="E-Mail" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-[#1A1A1A] border border-white/10 text-white p-2.5 outline-none focus:border-[#ff0033]" data-testid="trainer-email" />
        <input required type="text" placeholder="Passwort (min. 6)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-[#1A1A1A] border border-white/10 text-white p-2.5 outline-none focus:border-[#ff0033]" data-testid="trainer-pw" />
        <input placeholder="Trainername (für eigene Kurse)" value={form.trainer_name} onChange={(e) => setForm({ ...form, trainer_name: e.target.value })} className="bg-[#1A1A1A] border border-white/10 text-white p-2.5 outline-none focus:border-[#ff0033]" />
        <label className="flex items-center gap-2 text-sm text-neutral-300"><input type="checkbox" checked={form.can_see_all} onChange={(e) => setForm({ ...form, can_see_all: e.target.checked })} className="accent-[#ff0033]" /> Darf alle Kurse sehen</label>
        <button type="submit" disabled={creating} className="bg-[#ff0033] text-white font-heading uppercase tracking-wider py-2.5 hover:bg-[#cc0029] flex items-center justify-center gap-2 disabled:opacity-60" data-testid="trainer-create-submit">{creating ? <Loader2 className="animate-spin" size={16} /> : <><Plus size={16} /> Konto erstellen</>}</button>
      </form>

      {/* Liste */}
      <div className="bg-[#0F0F0F] border border-white/10 overflow-x-auto">
        <table className="w-full text-sm" data-testid="trainer-list">
          <thead><tr className="bg-[#1A1A1A] text-neutral-400 font-mono-lab text-[10px] uppercase tracking-widest">
            <th className="text-left p-3">Name</th><th className="text-left p-3">E-Mail</th><th className="text-left p-3">Alle Kurse</th>
            <th className="text-left p-3">Status</th><th className="text-left p-3">Letzter Login</th><th className="text-right p-3">Aktionen</th>
          </tr></thead>
          <tbody>
            {list.map((t) => (
              <tr key={t.id} className="border-t border-white/5 text-neutral-200" data-testid={`trainer-row-${t.id}`}>
                <td className="p-3 font-medium">{t.name}</td>
                <td className="p-3">{t.email}</td>
                <td className="p-3">
                  <button onClick={() => update(t.id, { can_see_all: !t.can_see_all })} className={`text-xs px-2 py-1 border ${t.can_see_all ? "border-green-500/40 text-green-400" : "border-white/20 text-neutral-400"}`}>{t.can_see_all ? "Ja" : "Nur eigene"}</button>
                </td>
                <td className="p-3"><span className={`text-xs px-2 py-1 ${t.active ? "text-green-400" : "text-neutral-500"}`}>{t.active ? "Aktiv" : "Deaktiviert"}</span></td>
                <td className="p-3 text-neutral-500 text-xs">{t.last_login ? new Date(t.last_login).toLocaleString("de-DE", { timeZone: "Europe/Berlin" }) : "—"}</td>
                <td className="p-3">
                  <div className="flex items-center justify-end gap-2">
                    <button title="Passwort setzen" onClick={() => { setPwFor(t); setPw(""); }} className="p-2 hover:text-[#ff0033]" data-testid={`trainer-pw-${t.id}`}><KeyRound size={15} /></button>
                    <button title={t.active ? "Deaktivieren" : "Aktivieren"} onClick={() => update(t.id, { active: !t.active })} className="p-2 hover:text-[#ff0033]" data-testid={`trainer-toggle-${t.id}`}><Power size={15} /></button>
                    <button title="Zugriffsprotokoll" onClick={() => openLog(t)} className="p-2 hover:text-[#ff0033]"><ScrollText size={15} /></button>
                    <button title="Löschen" onClick={() => del(t)} className="p-2 hover:text-red-500"><Trash2 size={15} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-neutral-500">Noch keine Trainerzugänge.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Passwort-Modal */}
      {pwFor && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center px-4" onClick={() => setPwFor(null)}>
          <div className="bg-[#0F0F0F] border border-white/10 p-6 w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4"><h3 className="font-heading uppercase text-white">Passwort setzen</h3><button onClick={() => setPwFor(null)}><X size={18} className="text-neutral-400" /></button></div>
            <p className="text-neutral-500 text-sm mb-3">Neues Passwort für <b className="text-white">{pwFor.name}</b></p>
            <input type="text" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Neues Passwort" className="w-full bg-[#1A1A1A] border border-white/10 text-white p-2.5 outline-none focus:border-[#ff0033]" data-testid="trainer-newpw" />
            <button onClick={resetPw} className="w-full mt-4 bg-[#ff0033] text-white font-heading uppercase tracking-wider py-2.5 hover:bg-[#cc0029]" data-testid="trainer-newpw-save">Speichern</button>
          </div>
        </div>
      )}

      {/* Log-Modal */}
      {logFor && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center px-4" onClick={() => setLogFor(null)}>
          <div className="bg-[#0F0F0F] border border-white/10 p-6 w-full max-w-lg max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4"><h3 className="font-heading uppercase text-white">Zugriffsprotokoll · {logFor.name}</h3><button onClick={() => setLogFor(null)}><X size={18} className="text-neutral-400" /></button></div>
            <div className="space-y-1 text-sm">
              {logs.map((l) => (
                <div key={l.id} className="flex justify-between border-b border-white/5 py-1.5 text-neutral-300">
                  <span><span className="text-[#ff0033]">{l.action}</span> {l.detail}</span>
                  <span className="text-neutral-500 text-xs">{new Date(l.at).toLocaleString("de-DE", { timeZone: "Europe/Berlin" })}</span>
                </div>
              ))}
              {logs.length === 0 && <p className="text-neutral-500">Keine Einträge.</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrainerManager;
